// RepoDetailPage.tsx — one repository: its current state, every run against
// it, and what actually landed in git.
//
// The three cards answer three different questions and are deliberately not
// merged. The header card is "is this repository documented and current". The
// run history is "what has docdaemon been doing about it" — including the runs
// that failed, which the header only summarises as a status word. The gitea
// panel is "did any of that reach the repository", which is the one thing
// docdaemon's own records cannot honestly answer: a run can believe it pushed.
//
// Every destructive control lives here rather than on the listing, behind the
// shared confirm dialog. A full reindex is not destructive but is expensive in
// exactly the way a misclick is worst — it re-documents every path in the
// repository — so it is confirmed too.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import {
  BsArrowClockwise, BsArrowRepeat, BsBook, BsBoxArrowUpRight, BsPauseFill, BsPlayFill, BsTrash,
} from 'react-icons/bs';
import { StatusBadge, ui } from 'common';
import type { AuthState } from 'common';
import { fetchRepo, isAdmin, pauseRepo, reindexRepo, removeRepo } from '../api';
import type { Repo, Run } from '../api';
import { EM_DASH, absoluteTime, relativeTime, shortSha } from '../format';
import GiteaPanel from '../components/GiteaPanel';
import RunTable from '../components/RunTable';
import { RepoStatusBadge } from '../components/StatusBadges';

export default function RepoDetailPage({ auth }: { auth: AuthState }) {
  const { owner = '', name = '' } = useParams();
  const navigate = useNavigate();
  const [confirm, confirmDialog] = ui.useConfirm();
  const [repo, setRepo] = useState<Repo | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const admin = isAdmin(auth);

  const load = async () => {
    try {
      const detail = await fetchRepo(owner, name);
      setRepo(detail.repo);
      setRuns(detail.runs);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repository');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [owner, name]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Remove Repository',
      body: <>Stop watching <code>{owner}/{name}</code>? Its recorded runs and documentation
        index are dropped here; the <code>.docs/</code> tree already committed to the
        repository stays where it is.</>,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await removeRepo(owner, name);
      navigate('/repos');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove repository');
      setBusy(false);
    }
  };

  const fullReindex = async () => {
    const ok = await confirm({
      title: 'Full Reindex',
      body: <>Re-document every path in <code>{owner}/{name}</code>, not just what changed?
        This is one LLM call per file plus one per directory — the whole first-run bill,
        again.</>,
      confirmLabel: 'Reindex Everything',
      variant: 'warning',
    });
    if (!ok) return;
    await act(() => reindexRepo(owner, name, true));
  };

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;
  }
  if (!repo) {
    return <Alert variant="danger">{error || 'Repository not found.'}</Alert>;
  }

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/repos' }}>Repositories</Breadcrumb.Item>
        {/* The full name is this thing's name — owner/name is what gitea calls
            it and what a person says out loud — so the trail states it rather
            than a router segment (DESIGN.md C-NAV-2). */}
        <Breadcrumb.Item active>{repo.full_name}</Breadcrumb.Item>
      </Breadcrumb>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card className="mb-4">
        <Card.Header className="d-flex align-items-center justify-content-between gap-2">
          <span className="text-truncate">{repo.full_name}</span>
          <span className="d-flex align-items-center gap-2 flex-shrink-0">
            {!repo.enabled && <StatusBadge tone="warning">paused</StatusBadge>}
            <RepoStatusBadge status={repo.status} />
          </span>
        </Card.Header>
        <Card.Body className="d-flex flex-column gap-3">
          {repo.last_error && (
            <Alert variant="warning" className="m-0 py-2 small">
              <Alert.Heading className="h6 m-0">Last run failed</Alert.Heading>
              {repo.last_error}
            </Alert>
          )}
          <Table size="sm" borderless className="m-0">
            <tbody>
              <tr>
                <td className="text-muted ps-0">Default branch</td>
                <td className="text-end pe-0"><code>{repo.default_branch || EM_DASH}</code></td>
              </tr>
              <tr>
                <td className="text-muted ps-0">Last indexed commit</td>
                <td className="text-end pe-0" title={absoluteTime(repo.last_indexed_at)}>
                  <code>{shortSha(repo.last_indexed_sha)}</code>
                  <span className="text-muted"> · {relativeTime(repo.last_indexed_at)}</span>
                </td>
              </tr>
              <tr>
                <td className="text-muted ps-0">Documents on disk</td>
                <td className="text-end pe-0">{repo.doc_count}</td>
              </tr>
              <tr>
                <td className="text-muted ps-0">Clone URL</td>
                <td className="text-end pe-0"><code className="small">{repo.clone_url}</code></td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
          <Link
            to={`/docs/${repo.owner}/${repo.name}`}
            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
          >
            <BsBook /> Docs
          </Link>
          <a
            href={repo.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
          >
            <BsBoxArrowUpRight /> Gitea
          </a>
          {admin && (
            <>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={busy || !repo.enabled}
                title={repo.enabled
                  ? 'Queue a run over the paths that changed since the last indexed commit'
                  : 'Resume watching this repository first'}
                onClick={() => void act(() => reindexRepo(owner, name, false))}
                className="d-inline-flex align-items-center gap-2"
              >
                <BsArrowClockwise /> Reindex
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                disabled={busy || !repo.enabled}
                onClick={() => void fullReindex()}
                className="d-inline-flex align-items-center gap-2"
              >
                <BsArrowRepeat /> Full Reindex
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={busy}
                onClick={() => void act(() => pauseRepo(owner, name, !repo.enabled))}
                className="d-inline-flex align-items-center gap-2"
              >
                {repo.enabled ? <><BsPauseFill /> Pause</> : <><BsPlayFill /> Resume</>}
              </Button>
              {/* Last, and the only one that is not undone by clicking it
                  again — separated from the rest by the auto margin so it is
                  not the neighbour of a control you use every day. */}
              <Button
                variant="outline-danger"
                size="sm"
                disabled={busy}
                onClick={() => void remove()}
                className="d-inline-flex align-items-center gap-2 ms-auto"
              >
                <BsTrash /> Remove
              </Button>
            </>
          )}
        </Card.Footer>
      </Card>

      <Card className="mb-4">
        <Card.Header>Run History</Card.Header>
        <Card.Body>
          <RunTable runs={runs} />
        </Card.Body>
        <Card.Footer className="small text-muted">
          {runs.length === 0
            ? 'Nothing has run against this repository yet.'
            : `${runs.length} run${runs.length === 1 ? '' : 's'} recorded — expand one for its jobs, deepest directories last.`}
        </Card.Footer>
      </Card>

      <GiteaPanel owner={owner} name={name} />

      {confirmDialog}
    </div>
  );
}
